import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Phone, Mail, Instagram, MapPin, Hammer, Zap, Car, SprayCan, Wrench, ChevronRight, Star, Quote } from "lucide-react";
import api, { waLink } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";

const iconMap = { hammer: Hammer, zap: Zap, car: Car, "spray-can": SprayCan, wrench: Wrench };
const DEFAULT_SECTION_ORDER = ["services", "offers", "gallery", "reviews", "about", "contact"];

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V7.69a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.12Z" />
  </svg>
);

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [offers, setOffers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [siteInfo, setSiteInfo] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/services"),
      api.get("/offers", { params: { only_active: true } }),
      api.get("/gallery"),
      api.get("/reviews", { params: { random: 6 } }),
      api.get("/site-info"),
    ]).then(([s, o, g, r, info]) => {
      setServices(s.data);
      setOffers(o.data);
      setGallery(g.data);
      setReviews(r.data);
      setSiteInfo(info.data);
    }).catch(console.error);
  }, []);

  const wa = siteInfo?.whatsapp || "";
  const hero = siteInfo?.hero || {};
  const about = siteInfo?.about || {};
  const sec = siteInfo?.sections || {};
  const heroStats = siteInfo?.hero_stats || [];
  const aboutStats = about?.stats || [];

  const order = (siteInfo?.section_order && siteInfo.section_order.length > 0)
    ? siteInfo.section_order
    : DEFAULT_SECTION_ORDER;
  const visibility = siteInfo?.section_visibility || {};
  // Append any missing default sections to keep them visible
  const fullOrder = [...order, ...DEFAULT_SECTION_ORDER.filter((k) => !order.includes(k))]
    .filter((k) => visibility[k] !== false);

  const sectionMap = {
    services: (
      <section key="services" id="servizi" className="py-24 sm:py-32 bg-palumbo-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-palumbo-copper mb-3">— {sec.services_badge}</div>
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">
                {sec.services_title} <span className="text-palumbo-copper">{sec.services_title_highlight}</span>
              </h2>
            </div>
            <p className="text-neutral-400 max-w-md leading-relaxed">{sec.services_description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((svc, i) => {
              const Icon = iconMap[svc.icon] || Wrench;
              return (
                <motion.div key={svc.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`service-card-${i}`}
                  className="group relative bg-palumbo-dark border border-white/10 hover:border-palumbo-copper transition-all duration-300 overflow-hidden cursor-pointer">
                  <div className="relative h-56 overflow-hidden">
                    {svc.image_url && <img src={svc.image_url} alt={svc.title} className="w-full h-full object-cover opacity-50 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-palumbo-dark via-palumbo-dark/40 to-transparent"></div>
                    <div className="absolute top-4 left-4 w-12 h-12 bg-black/70 border border-palumbo-copper flex items-center justify-center">
                      <Icon size={22} strokeWidth={1.5} className="text-palumbo-copper" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-bold text-xl uppercase mb-3">{svc.title}</h3>
                    <p className="text-sm text-neutral-400 leading-relaxed mb-4 min-h-[60px]">{svc.description}</p>
                    <a href={waLink(wa, `Ciao, vorrei info su: ${svc.title}`)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 text-palumbo-copper text-xs font-bold uppercase tracking-widest group-hover:gap-3 transition-all">
                      Richiedi info <ChevronRight size={14} />
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    ),

    offers: offers.length > 0 ? (
      <section key="offers" id="offerte" className="py-24 sm:py-32 bg-gradient-to-b from-palumbo-black to-palumbo-dark relative overflow-hidden border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-palumbo-copper mb-3">— {sec.offers_badge}</div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">
              {sec.offers_title} <span className="text-palumbo-copper">{sec.offers_title_highlight}</span>
            </h2>
            <p className="text-neutral-400 max-w-md">{sec.offers_description}</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {offers.map((o, i) => (
            <motion.div key={o.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              data-testid={`offer-card-${i}`}
              className="bg-palumbo-dark border border-white/10 hover:border-palumbo-copper p-8 transition-all duration-300 group relative">
              {o.discount && (
                <div className="absolute -top-3 left-6 bg-palumbo-copper text-white text-xs font-bold uppercase tracking-widest px-3 py-1">{o.discount}</div>
              )}
              <h3 className="font-display font-bold text-2xl uppercase mb-4 mt-3 leading-tight">{o.title}</h3>
              <p className="text-neutral-400 text-sm leading-relaxed mb-6">{o.description}</p>
              <a href={waLink(wa, `Ciao, sono interessato all'offerta: ${o.title}`)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-palumbo-copper text-xs font-bold uppercase tracking-widest group-hover:gap-3 transition-all">
                Approfitta ora <ArrowRight size={14} />
              </a>
            </motion.div>
          ))}
        </div>
      </section>
    ) : null,

    gallery: (
      <section key="gallery" id="lavori" className="py-24 sm:py-32 bg-palumbo-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-palumbo-copper mb-3">— {sec.gallery_badge}</div>
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">{sec.gallery_title}</h2>
            </div>
            <p className="text-neutral-400 max-w-md">{sec.gallery_description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {gallery.map((item, i) => (
              <motion.div key={item.id} initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                data-testid={`gallery-item-${i}`}
                className={`group relative overflow-hidden bg-palumbo-dark border border-white/10 ${i === 0 ? "md:col-span-2 md:row-span-2" : ""} ${i === 3 ? "md:col-span-2" : ""}`}>
                <div className={`relative ${i === 0 ? "h-[300px] md:h-[616px]" : "h-[300px]"}`}>
                  <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent opacity-90"></div>
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-palumbo-copper mb-2">{item.category}</div>
                    <div className="font-display font-bold text-xl uppercase">{item.title}</div>
                    {item.description && <p className="text-neutral-400 text-sm mt-2 max-w-md">{item.description}</p>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    ),

    reviews: reviews.length > 0 ? (
      <section key="reviews" id="recensioni" className="py-24 sm:py-32 bg-palumbo-black border-t border-white/5 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.3em] text-palumbo-copper mb-3">— {sec.reviews_badge || "Dicono di noi"}</div>
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">
                {sec.reviews_title || "Recensioni"} <span className="text-palumbo-copper">{sec.reviews_title_highlight || "clienti"}</span>
              </h2>
            </div>
            <p className="text-neutral-400 max-w-md leading-relaxed">{sec.reviews_description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`review-card-${i}`}
                className="relative bg-palumbo-dark border border-white/10 hover:border-palumbo-copper p-8 transition-all duration-300 group">
                <Quote className="absolute top-6 right-6 text-palumbo-copper/20 group-hover:text-palumbo-copper/40 transition-colors" size={40} strokeWidth={1} />
                <div className="flex items-center gap-1 mb-5">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={16} className={s < (r.rating || 5) ? "text-palumbo-copper fill-palumbo-copper" : "text-white/15"} />
                  ))}
                </div>
                <p className="text-neutral-300 leading-relaxed text-sm mb-6 relative z-10">&ldquo;{r.text}&rdquo;</p>
                <div className="border-t border-white/10 pt-4">
                  <div className="font-display font-bold uppercase tracking-tight">{r.name}</div>
                  {r.city && <div className="text-[10px] uppercase tracking-widest text-palumbo-copper mt-1">{r.city}</div>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    ) : null,

    about: (
      <section key="about" id="chi-siamo" className="py-24 sm:py-32 bg-palumbo-dark border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] overflow-hidden">
            {about.image_url && <img src={about.image_url} alt="Officina" className="w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-tr from-palumbo-black/60 via-transparent to-transparent"></div>
            {about.badge_overlay && (
              <div className="absolute bottom-6 left-6 bg-palumbo-copper text-black px-4 py-2 font-display font-black uppercase text-sm tracking-tight">
                {about.badge_overlay}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-palumbo-copper mb-3">— {about.badge}</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl uppercase tracking-tighter mb-6 leading-[0.95]">
              {about.title_line1}<br /><span className="text-palumbo-copper">{about.title_highlight}</span>
            </h2>
            <p className="text-neutral-300 leading-relaxed mb-6 whitespace-pre-line">{about.paragraph1}</p>
            {about.paragraph2 && <p className="text-neutral-400 leading-relaxed mb-8 whitespace-pre-line">{about.paragraph2}</p>}
            {aboutStats.length > 0 && (
              <div className="grid grid-cols-2 gap-6 border-t border-white/10 pt-8">
                {aboutStats.map((s, i) => (
                  <div key={i}>
                    <div className="font-display font-black text-3xl text-palumbo-copper">{s.value}</div>
                    <div className="text-xs uppercase tracking-widest text-neutral-400 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    ),

    contact: (
      <section key="contact" id="contatti" className="py-24 sm:py-32 bg-palumbo-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-palumbo-copper mb-3">— {sec.contact_badge}</div>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mb-6">
              {sec.contact_title_line1}<br /><span className="text-palumbo-copper">{sec.contact_title_highlight}</span>
            </h2>
            <p className="text-neutral-400 max-w-2xl mx-auto whitespace-pre-line">{sec.contact_description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <a href={waLink(wa)} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
              className="group bg-palumbo-dark border border-white/10 hover:border-palumbo-copper p-8 transition-all duration-300 flex flex-col items-start gap-4">
              <MessageCircle size={28} strokeWidth={1.5} className="text-palumbo-copper" />
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">WhatsApp</div>
                <div className="font-display font-bold text-lg group-hover:text-palumbo-copper transition-colors">Scrivici subito</div>
              </div>
            </a>
            <a href={`tel:${siteInfo?.phone?.replace(/\s/g, "") || ""}`} data-testid="contact-phone"
              className="group bg-palumbo-dark border border-white/10 hover:border-palumbo-copper p-8 transition-all duration-300 flex flex-col items-start gap-4">
              <Phone size={28} strokeWidth={1.5} className="text-palumbo-copper" />
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Telefono</div>
                <div className="font-display font-bold text-base group-hover:text-palumbo-copper transition-colors break-all">{siteInfo?.phone}</div>
              </div>
            </a>
            <a href={`mailto:${siteInfo?.email || ""}`} data-testid="contact-email"
              className="group bg-palumbo-dark border border-white/10 hover:border-palumbo-copper p-8 transition-all duration-300 flex flex-col items-start gap-4">
              <Mail size={28} strokeWidth={1.5} className="text-palumbo-copper" />
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Email</div>
                <div className="font-display font-bold text-sm group-hover:text-palumbo-copper transition-colors break-all">{siteInfo?.email}</div>
              </div>
            </a>
            <div className="bg-palumbo-dark border border-white/10 p-8 flex flex-col items-start gap-4">
              <MapPin size={28} strokeWidth={1.5} className="text-palumbo-copper" />
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Indirizzo</div>
                <div className="font-display font-bold text-base">{siteInfo?.address}</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <span className="text-xs uppercase tracking-widest text-neutral-500">Seguici sui social</span>
            <div className="flex gap-3">
              <a href={siteInfo?.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram"
                className="w-12 h-12 border border-white/15 flex items-center justify-center hover:bg-palumbo-copper hover:border-palumbo-copper transition-colors">
                <Instagram size={18} strokeWidth={1.5} />
              </a>
              <a href={siteInfo?.tiktok} target="_blank" rel="noreferrer" data-testid="contact-tiktok"
                className="w-12 h-12 border border-white/15 flex items-center justify-center hover:bg-palumbo-copper hover:border-palumbo-copper transition-colors">
                <TikTokIcon className="w-[18px] h-[18px]" />
              </a>
            </div>
          </div>
        </div>
      </section>
    ),
  };

  return (
    <div className="bg-palumbo-black text-white min-h-screen font-body">
      <Header siteInfo={siteInfo} />
      <WhatsAppFAB phone={wa} />

      {/* HERO (fisso in alto) */}
      <section className="relative min-h-screen flex items-center overflow-hidden" id="home">
        <div className="absolute inset-0">
          {hero.image_url && <img src={hero.image_url} alt="Palumbo Custom Garage" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full pt-32 pb-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            {hero.badge && (
              <div className="flex items-center gap-3 mb-6">
                <span className="w-12 h-[2px] bg-palumbo-copper"></span>
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-palumbo-copper">{hero.badge}</span>
              </div>
            )}
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.95] tracking-tighter mb-6">
              {hero.title_line1}<br />
              <span className="text-palumbo-copper">{hero.title_highlight}</span> {hero.title_line2}
            </h1>
            <p className="text-lg md:text-xl text-neutral-300 max-w-2xl leading-relaxed mb-10 whitespace-pre-line">{hero.subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#servizi" data-testid="hero-cta-services"
                className="inline-flex items-center justify-center gap-2 bg-palumbo-copper hover:bg-palumbo-copper-dark text-white font-bold uppercase tracking-wide px-8 py-4 text-sm transition-colors">
                {hero.cta_primary || "Scopri i Servizi"} <ArrowRight size={16} />
              </a>
              <a href={waLink(wa)} target="_blank" rel="noreferrer" data-testid="hero-cta-whatsapp"
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-palumbo-copper hover:text-palumbo-copper text-white font-bold uppercase tracking-wide px-8 py-4 text-sm transition-colors">
                <MessageCircle size={16} /> {hero.cta_secondary || "Richiedi un Preventivo"}
              </a>
            </div>
          </motion.div>
          {heroStats.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-20 lg:mt-28 hidden lg:flex justify-between items-end gap-12 border-t border-white/15 pt-8">
              {heroStats.map((stat, i) => (
                <div key={i}>
                  <div className="font-display font-black text-3xl lg:text-5xl text-white">{stat.value}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-neutral-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Sezioni renderizzate in base a section_order */}
      {fullOrder.map((key) => sectionMap[key]).filter(Boolean)}

      <Footer siteInfo={siteInfo} />
    </div>
  );
}
